import React, { useState, useEffect, useRef } from "react";
import { FaChevronCircleUp, FaChevronLeft, FaChevronRight } from "react-icons/fa";

const StakeholderProfileForm = ({
  stakeholder,
  stakeholderNumber,
  initialData,
  onComplete,
  onDataChange,
  isSaving,
  loading,
}) => {
  const [values, setValues] = useState(initialData?.values || ["", "", ""]);
  const [kpis, setKpis] = useState(initialData?.kpis || ["", "", ""]);
  const [interests, setInterests] = useState(initialData?.interests || ["", "", ""]);

  const isInitializing = useRef(true);

  // Initialize form state once on mount or initialData change
  useEffect(() => {
    if (initialData && isInitializing.current) {
      setValues(initialData.values || ["", "", ""]);
      setKpis(initialData.kpis || ["", "", ""]);
      setInterests(initialData.interests || ["", "", ""]);
      setTimeout(() => {
        isInitializing.current = false;
      }, 100);
    }
  }, [initialData]);

  // Call onDataChange only on user edits (not during initialization)
  useEffect(() => {
    if (isInitializing.current) return;
    if (onDataChange) {
      console.log("📤 onDataChange triggered:", { values, kpis, interests });
      onDataChange({
        values,
        kpis,
        interests,
      });
    }
  }, [values, kpis, interests, onDataChange]);

  const handleValueChange = (index, value) => {
    const newValues = [...values];
    newValues[index] = value;
    setValues(newValues);
  };

  const handleKpiChange = (index, value) => {
    const newKpis = [...kpis];
    newKpis[index] = value;
    setKpis(newKpis);
  };

  const handleInterestChange = (index, value) => {
    const newInterests = [...interests];
    newInterests[index] = value;
    setInterests(newInterests);
  };

  const addMoreKpi = () => setKpis([...kpis, ""]);
  const addMoreInterest = () => setInterests([...interests, ""]);

  const removeKpi = (index) => {
    if (kpis.length > 3) setKpis(kpis.filter((_, i) => i !== index));
  };

  const removeInterest = (index) => {
    if (interests.length > 3) setInterests(interests.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    const formData = {
      values: values.filter((v) => v.trim() !== ""),
      kpis: kpis.filter((k) => k.trim() !== ""),
      interests: interests.filter((i) => i.trim() !== ""),
    };
    onComplete(formData);
  };

  const isValid =
    values.some((v) => v.trim() !== "") &&
    kpis.some((k) => k.trim() !== "") &&
    interests.some((i) => i.trim() !== "");

  const addMoreValues = () => {
    setValues([...values, ""]);
  };

  const removeValue = (index) => {
    if (values.length > 3) {
      setValues(values.filter((_, i) => i !== index));
    }
  };

  return (
    <>
    <div className="w-full h-auto min-h-screen overflow-y-auto bg-white relative">
      {/* Navigation Buttons */}
            <div className="flex items-center justify-end gap-4 pt-4 absolute right-2 -top-4">
              {/* <button
                onClick={handleSubmit}
                disabled={!isValid || loading}
                className={`inline-flex items-center gap-2 px-8 py-4 rounded-full text-white font-gotham font-medium text-lg transition-all ${
                  isValid && !loading ? "bg-[#54B0AF] hover:bg-teal-600 shadow-lg hover:shadow-xl cursor-pointer" : "bg-gray-300 cursor-not-allowed"
                }`}
              >
                {loading ? (
                  "Saving..."
                ) : (
                  <>
                    Complete Profile <FaChevronCircleUp className="rotate-90 ml-2" />
                  </>
                )}
              </button> */}

              <div className="flex items-center gap-2">
                {/* Back button */}
                <button
                  disabled={stakeholderNumber === 1}
                  className="inline-flex items-center gap-2 px-1.5 py-1.5 bg-[#B6B6B6] justify-center rounded-full hover:bg-[#54B0AF] font-medium transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <FaChevronLeft className="w-5 h-5 font-gotham font-bold text-white" />
                </button>

                {/* Next button */}
                <button
                  onClick={handleSubmit}
                  disabled={!isValid || loading}
                  className="inline-flex items-center gap-2 px-1.5 py-1.5 bg-[#54B0AF] justify-center rounded-full hover:bg-[#439F9E] font-medium transition-all disabled:opacity-30 disabled:bg-[#B6B6B6] disabled:cursor-not-allowed"
                >
                  <FaChevronRight className="w-5 h-5 font-gotham font-bold text-white" />
                </button>
              </div>
            </div>
      <div className="max-w-full mx-auto px-4 md:px-0 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-2">
          {/* Left Side - Form */}
          <div className="lg:col-span-2 space-y-8">
            {/* Section A: Core Values */}
            <FormSection
              label="A"
              title={`Which values define ${stakeholder?.name?.split(" ")[0]}'s approach to work?`}
              subtitle="Write down the three core values that your stakeholder carries"
            >
              {values.map((value, index) => (
                <div key={`value-${index}`} className="relative">
                  <FormInput
                    label={`${index + 1}.`}
                    value={value}
                    onChange={(e) => handleValueChange(index, e.target.value)}
                    placeholder={`Value ${index + 1}`}
                  />

                  {index >= 3 && (
                    <button
                      onClick={() => removeValue(index)}
                      className="absolute right-0 top-0 text-[#54B0AF] hover:text-teal-600 p-1"
                      title="Remove"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}

              {/* ADD MORE BUTTON — THIS WAS MISSING */}
              <button
                onClick={addMoreValues}
                className="flex items-center gap-2 text-[#54B0AF] hover:text-teal-600 font-gotham font-medium transition-colors mt-2"
              >
                <span className="text-xl">+</span>Add More
              </button>
            </FormSection>

            {/* Section B: KPIs */}
            <FormSection
              label="B"
              title={`What do you think can help ${stakeholder?.name?.split(" ")[0]} get promoted?`}
              subtitle="Write down the three KPIs that influence their promotion"
            >
              {kpis.map((kpi, index) => (
                <div key={`kpi-${index}`} className="relative">
                  <FormInput
                    label={`${index + 1}.`}
                    value={kpi}
                    onChange={(e) => handleKpiChange(index, e.target.value)}
                    placeholder={`Key Performance Indicator ${index + 1}`}
                  />
                  {index >= 3 && (
                    <button
                      onClick={() => removeKpi(index)}
                      className="absolute right-0 top-0 text-[#54B0AF] hover:text-teal-600 p-1"
                      title="Remove"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={addMoreKpi}
                className="flex items-center gap-2 text-[#54B0AF] hover:text-teal-600 font-gotham font-medium transition-colors mt-2"
              >
                <span className="text-xl">+</span>Add More
              </button>
            </FormSection>

            {/* Section C: Likes & Interests */}
            <FormSection
              label="C"
              title={`What kind of things does ${stakeholder?.name?.split(" ")[0]} find interesting?`}
              subtitle="Write down three likes and interests of the stakeholder"
            >
              {interests.map((interest, index) => (
                <div key={`interest-${index}`} className="relative">
                  <FormInput
                    label={`${index + 1}.`}
                    value={interest}
                    onChange={(e) => handleInterestChange(index, e.target.value)}
                    placeholder={`Likes & Interests ${index + 1}`}
                  />
                  {index >= 3 && (
                    <button
                      onClick={() => removeInterest(index)}
                      className="absolute right-0 top-0 text-[#54B0AF] hover:text-teal-600 p-1"
                      title="Remove"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={addMoreInterest}
                className="flex items-center gap-2 text-[#54B0AF] hover:text-teal-600 font-gotham font-medium transition-colors mt-2"
              >
                <span className="text-xl md:text-lg">+</span>Add More
              </button>
            </FormSection>

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between gap-4 pt-4">
              <button
                onClick={handleSubmit}
                disabled={!isValid || loading}
                className={`inline-flex items-center gap-2 px-8 py-4 rounded-full text-white font-gotham font-medium text-lg transition-all ${
                  isValid && !loading ? "bg-[#54B0AF] hover:bg-teal-600 shadow-lg hover:shadow-xl cursor-pointer" : "bg-gray-300 cursor-not-allowed"
                }`}
              >
                {loading ? (
                  "Saving..."
                ) : (
                  <>
                    Complete Profile <FaChevronCircleUp className="rotate-90 ml-2" />
                  </>
                )}
              </button>

              <div className="flex items-center gap-2">
                {/* Back button */}
                <button
                  disabled={stakeholderNumber === 1}
                  className="inline-flex items-center gap-2 px-1.5 py-1.5 bg-[#B6B6B6] justify-center rounded-full hover:bg-[#54B0AF] font-medium transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <FaChevronLeft className="w-5 h-5 font-gotham font-bold text-white" />
                </button>

                {/* Next button */}
                <button
                  onClick={handleSubmit}
                  disabled={!isValid || loading}
                  className="inline-flex items-center gap-2 px-1.5 py-1.5 bg-[#54B0AF] justify-center rounded-full hover:bg-[#439F9E] font-medium transition-all disabled:opacity-30 disabled:bg-[#B6B6B6] disabled:cursor-not-allowed"
                >
                  <FaChevronRight className="w-5 h-5 font-gotham font-bold text-white" />
                </button>
              </div>
            </div>

            {/* {isSaving && <p className="text-sm text-gray-500 italic">Saving progress...</p>} */}
          </div>

          {/* Right Side - Stakeholder Card Preview */}
          <div className="lg:col-span-1">
            <div className="sticky top-8">
              <StakeholderCardPreview
                stakeholder={stakeholder}
                stakeholderNumber={stakeholderNumber}
                values={values.filter((v) => v.trim() !== "")}
                kpis={kpis.filter((k) => k.trim() !== "")}
                interests={interests.filter((i) => i.trim() !== "")}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

// Form Section Component
const FormSection = ({ label, title, subtitle, children }) => {
  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
      <div className="flex gap-4 mb-6">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#E6E6E6] flex items-center justify-center font-bold text-[#444444] border-[1.66px] border-[#CCCCCC]">
          {label}
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-bold text-[#323232] font-gotham mb-1">{title}</h3>
          <p className="text-sm md:text-[15px] text-[#888888]">{subtitle}</p>
        </div>
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
};

// Form Input Component
const FormInput = ({ label, value, onChange, placeholder }) => {
  return (
    <div className="flex items-center space-x-3 mb-6 ml-12">
      <label className="text-lg font-medium font-gotham text-black">{label}</label>
      <input
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="flex-1 border-b border-[#000000] focus:border-[#54B0AF] font-gotham focus:outline-none text-[#54B0AF] font-medium text-lg placeholder-[#B8B8B8] transition-all"
      />
    </div>
  );
};

// Stakeholder Card Preview Component
const StakeholderCardPreview = ({
  stakeholder,
  stakeholderNumber,
  values,
  kpis,
  interests,
}) => {
  const gradientClasses = {
    1: "from-[#5303BB] to-[#160131]",
    2: "from-[#FF6B35] to-[#F7931E]",
  };

  const gradient = gradientClasses[stakeholderNumber] || gradientClasses[1];

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200 max-w-[429px]">
      <div
        className={`bg-gradient-to-r ${gradient} py-4 h-[130px] 2xl:h-[146px] px-6 text-white text-center relative`}
        // style={{ height: "146px" }}
      >
        <p className="text-sm lg:text-[12px] 2xl:text-[14px] font-medium font-gotham text-white uppercase tracking-wide mb-3">
          STAKEHOLDER #{String(stakeholderNumber).padStart(2, "0")}
        </p>
        <div className="absolute left-1/2 transform -translate-x-1/2 top-12 2xl:top-12 w-[130px] h-[130px] 2xl:w-[163.977294921875px] 2xl:h-[163.977294921875px] rounded-full bg-[#F1F1F1] border-[2.76px] border-[#E8E8E8] shadow-xl flex items-center justify-center text-8xl">
          {stakeholder?.avatar ? (
            stakeholder.avatar.startsWith("http") ||
            stakeholder.avatar.startsWith("/static") ||
            stakeholder.avatar.startsWith("/media") ? (
              <img
                src={stakeholder.avatar}
                alt={stakeholder.name}
                className="2xl:w-32 2xl:h-32 w-24 h-24 object-contain rounded-full"
              />
            ) : (
              <span className="text-7xl">{stakeholder.avatar}</span>
            )
          ) : (
            "🦁"
          )}
        </div>
      </div>

      <div className="pt-16 pb-5 2xl:pb-6 px-6 space-y-6">
        <div className="text-center pb-3 2xl:pb-6 border-b border-[#E3E3E3] mt-1 2xl:mt-3">
          <h3 className="text-xl lg:text-[22px] 2xl:text-[23px] font-medium text-[#1C1C1C] font-gotham mb-1">
            {stakeholder?.name || ""}
          </h3>
          <p className="text-xs lg:text-[12px] 2xl:text-[14px] text-[#1C1C1C] font-gotham font-normal uppercase tracking-wide">
            {stakeholder?.role || ""}
          </p>
        </div>

        <div>
          <h4 className="text-xs lg:text-[11px] 2xl:text-[12px] font-bold text-[#000000] font-gotham uppercase tracking-wide mb-2 2xl:mb-3 text-center">
            Their Core Values
          </h4>
          <div className="flex flex-wrap justify-center gap-2">
            {values.length > 0 ? (
              values.map((value, index) => (
                <span
                  key={index}
                  className="px-3 py-1.5 bg-black text-white text-xs rounded-full font-medium"
                >
                  {value}
                </span>
              ))
            ) : (
              <>
                <div className="w-[91.67637634277344px] h-7 bg-[#CCCCCC] rounded-full animate-pulse"></div>
                <div className="w-[91.67637634277344px] h-7 bg-[#CCCCCC] rounded-full animate-pulse"></div>
                <div className="w-[91.67637634277344px] h-7 bg-[#CCCCCC] rounded-full animate-pulse"></div>
              </>
            )}
          </div>
        </div>

        <div>
          <h4 className="text-xs lg:text-[11px] 2xl:text-[12px] font-bold text-[#000000] font-gotham uppercase tracking-wide mb-2 text-center">
            Things That Get Them Promoted
          </h4>
          <ul className="space-y-1">
            {kpis.length > 0 ? (
              kpis.map((kpi, index) => (
                <li
                  key={index}
                  className="flex items-start gap-2 text-sm text-[#323232] font-gotham font-normal lg:text-[13px] 2xl:text-[16px]"
                >
                  <span className="text-[#323232] mt-0.5">•</span>
                  <span>{kpi}</span>
                </li>
              ))
            ) : (
              <>
                <li className="flex items-start gap-2">
                  <span className="text-[#D9D9D9] text-4xl">•</span>
                  <div className="flex-1 h-4 mt-2 bg-[#F0F0F0] rounded-[10px] animate-pulse"></div>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#D9D9D9] text-4xl">•</span>
                  <div className="flex-1 h-4 mt-2 bg-[#F0F0F0] rounded-[10px] animate-pulse"></div>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#D9D9D9] text-4xl">•</span>
                  <div className="flex-1 h-4 mt-2 bg-[#F0F0F0] rounded-[10px] animate-pulse"></div>
                </li>
              </>
            )}
          </ul>
        </div>

        <div>
          <h4 className="text-xs lg:text-[11px] 2xl:text-[12px] font-bold text-[#323232] uppercase tracking-wide mb-3 text-center">
            Likes & Interests
          </h4>
          <ul className="space-y-2">
            {interests.length > 0 ? (
              interests.map((interest, index) => (
                <li
                  key={index}
                  className="flex items-start gap-2 text-sm text-[#323232] font-gotham font-normal lg:text-[13px] 2xl:text-[16px]"
                >
                  <span className="text-[#323232] mt-0.5">•</span>
                  <span>{interest}</span>
                </li>
              ))
            ) : (
              <>
                <li className="flex items-start gap-2">
                  <span className="text-[#D9D9D9] text-4xl">•</span>
                  <div className="flex-1 h-4 mt-2 bg-[#F0F0F0] rounded-[10px] animate-pulse"></div>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#D9D9D9] text-4xl">•</span>
                  <div className="flex-1 h-4 mt-2 bg-[#F0F0F0] rounded-[10px] animate-pulse"></div>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#D9D9D9] text-4xl">•</span>
                  <div className="flex-1 h-4 mt-2 bg-[#F0F0F0] rounded-[10px] animate-pulse"></div>
                </li>
              </>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default StakeholderProfileForm;